import { CheckIcon, PlusIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import Button from 'components/inputs/button';
import {
  BasicProductData,
  EnrichedAccessory
} from 'components/layout/modals/compatible-combinations-browser-modal';
import { KeyboardEvent, ReactNode } from 'react';
import { computeCombinationTotalPrice } from 'utils/combination-pricing';
import { KEPT_PAIRING_TAG } from 'utils/compatibility-systems';
import { readDuctedOperatingCost } from 'utils/ducted-system-metrics';
import { formatPrice } from 'utils/helpers';
import { formatAnnualOperatingCostLine } from 'utils/operating-cost-format';
import { canSelectCombination } from 'utils/quotable-add-pipeline';
import { combinationConfigLines } from './combination-config-lines';
import {
  CARD_COMPARE_LABELS,
  compareActionText,
  CompareActionLabels
} from './compare-action-text';

// Card for one compatible-combination row in the ducted Systems rail
// (AHA-6681 CS-2). Visual shell mirrors TopPickCard (border, selected ring,
// title/price row, config lines, bottom action row). The Compare button is
// wired to the shared useQuotableAdd pathway hosted by the rail (AHA-6681):
// the card only reports the toggle via `onToggleCompare` and renders the
// added state from `isAddedToCompare`.
export type DuctedCombinationCardProps = Readonly<{
  combination: Record<string, unknown>;
  productData: Record<string, BasicProductData>;
  accessoryData: Record<string, EnrichedAccessory>;
  title: string;
  // Extra type line, rendered only when it differs from the title. Every
  // card's title is already the component composition.
  typeLabel?: string;
  // Kept-constrained result-set marker (AHA-6681 sub-build 4): renders the
  // "Pairs with Customer's Equipment" chip so the distinction survives when
  // the card's data leaves its rail sub-section (detail panel, comparison,
  // quote).
  keptPairing?: boolean;
  // Furnace-only row under an active tonnage filter (AHA-6681 tonnage §C
  // polish): these combos PASS the size filter server-side (a cooling-size
  // filter must not hide heating-only systems the wants asked for), so the
  // card must explain itself next to the sized header or it reads as a bug.
  noCoolingSide?: boolean;
  // Actively-filtered ratings the server could not evaluate for this kept
  // pairing (AHA-6681 derive-aware filtering): those filters null-passed
  // rather than hide the user's own equipment, and the card explains why
  // the row is present despite the filter.
  unevaluatedFilterLabels?: string[];
  isSelected: boolean;
  onSelect: () => void;
  // Compare toggle (AHA-6681 card-level wiring): true while the system this
  // card added is still in the comparison tray. The rail owns the add/remove
  // pathway; the card only reports the click.
  isAddedToCompare?: boolean;
  isAdding?: boolean;
  onToggleCompare?: () => void;
  compareLabels?: CompareActionLabels;
  modelSpecs?: Record<string, Record<string, unknown>>;
  details?: ReactNode;
  testId?: string;
}>;

export default function DuctedCombinationCard({
  combination,
  productData,
  accessoryData,
  title,
  typeLabel,
  modelSpecs,
  keptPairing,
  noCoolingSide,
  unevaluatedFilterLabels,
  isSelected,
  onSelect,
  isAddedToCompare = false,
  isAdding = false,
  onToggleCompare,
  compareLabels = CARD_COMPARE_LABELS,
  details,
  testId
}: DuctedCombinationCardProps) {
  const priceResult = computeCombinationTotalPrice(
    combination,
    productData,
    accessoryData
  );

  // Same guard as the detail panel's Add button: an unresolvable slot (real
  // model missing from the catalog enrichment) must disable the add.
  const selectable = canSelectCombination(combination, productData);

  const configLines = combinationConfigLines(combination, modelSpecs);
  const showTypeLabel = Boolean(typeLabel) && typeLabel !== title;

  const handleCardKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelect();
    }
  };

  let priceNode: React.ReactNode;
  if (priceResult.kind === 'total') {
    priceNode = (
      <div className='shrink-0 font-bold text-gray-900'>
        {formatPrice(priceResult.total)}
      </div>
    );
  } else if (priceResult.kind === 'unavailable') {
    priceNode = (
      <div className='shrink-0 text-xs text-gray-400'>Price unavailable</div>
    );
  } else {
    priceNode = <div className='shrink-0 font-bold text-gray-400'>—</div>;
  }

  const annualOperatingCost =
    readDuctedOperatingCost(combination)?.annualTotalCost ?? null;

  return (
    <div
      role='button'
      tabIndex={0}
      data-testid={testId}
      onClick={onSelect}
      onKeyDown={handleCardKeyDown}
      className={clsx(
        'group relative flex h-full w-full cursor-pointer flex-col rounded-lg border p-4 text-left shadow-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
        isSelected
          ? 'border-blue-500 bg-blue-50 ring-[3px] ring-blue-100'
          : 'border-gray-200 bg-white hover:border-blue-300'
      )}
    >
      <div className='grow'>
        <div className='flex items-start justify-between gap-2'>
          <div className='font-semibold text-gray-900'>{title}</div>
          <div className='shrink-0 text-right'>
            {priceNode}
            {annualOperatingCost !== null && (
              <div className='mt-0.5 text-xs font-normal text-gray-500'>
                {formatAnnualOperatingCostLine(annualOperatingCost)}
              </div>
            )}
          </div>
        </div>
        {showTypeLabel && (
          <div className='mt-0.5 text-xs text-gray-500'>{typeLabel}</div>
        )}
        {keptPairing && (
          <div className='mt-1'>
            <span className='inline-flex items-center rounded bg-green-100 px-2 py-0.5 text-[11px] font-semibold text-green-900'>
              {KEPT_PAIRING_TAG}
            </span>
          </div>
        )}
        {noCoolingSide && (
          <div className='mt-1'>
            <span className='inline-flex items-center rounded-full border border-gray-300 bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-600'>
              No cooling side
            </span>
            <span className='ml-1.5 text-[11px] text-gray-500'>
              Heating-only — the size filter doesn&apos;t apply
            </span>
          </div>
        )}
        {unevaluatedFilterLabels && unevaluatedFilterLabels.length > 0 && (
          <div className='mt-1'>
            <span className='inline-flex items-center rounded-full border border-gray-300 bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-600'>
              No {unevaluatedFilterLabels.join(' / ')} data
            </span>
            <span className='ml-1.5 text-[11px] text-gray-500'>
              Kept pairing — this rating can&apos;t be filtered
            </span>
          </div>
        )}
        <div className='mt-2 flex flex-col text-xs text-gray-500'>
          {configLines.map((line) => (
            <div key={line.label}>
              {line.label}: {line.text}
            </div>
          ))}
        </div>
        {details}
      </div>
      {onToggleCompare && (
        <div
          className={clsx(
            'mt-3 flex items-center justify-end gap-2 border-t pt-2',
            isSelected ? 'border-blue-100' : 'border-gray-100'
          )}
        >
          <Button
            color={isAddedToCompare ? 'green' : 'secondary'}
            text={compareActionText(isAdding, isAddedToCompare, compareLabels)}
            icon={isAddedToCompare ? CheckIcon : PlusIcon}
            iconSize='sm'
            loading={isAdding}
            disabled={isAdding || (!isAddedToCompare && !selectable)}
            tooltip={
              isAddedToCompare || selectable
                ? undefined
                : 'Product not available in catalog'
            }
            onClick={(event) => {
              event?.stopPropagation();
              onToggleCompare();
            }}
          />
        </div>
      )}
    </div>
  );
}
